import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/CloudQueue';
import DeviceIcon from '@mui/icons-material/PhoneAndroid';
import { useAuthStore } from '../../stores/authStore';
import { manifestSyncService } from '../../services/manifestSyncService';
import { useWarbandStore } from '../../stores/warbandStore';
import { getAllWarbands } from '../../services/localStorageService';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

function entityLabel(entityKey: string): string {
  const [kind, ...idParts] = entityKey.split(':');
  const id = idParts.join(':');
  switch (kind) {
    case 'warband': return `Warband ${id}`;
    case 'custom-creature': return `Custom creature ${id}`;
    case 'custom-weapon': return `Custom weapon ${id}`;
    case 'custom-armour-item': return `Custom armour ${id}`;
    case 'custom-buff': return `Custom effect ${id}`;
    default: return entityKey;
  }
}

export const SyncEntityConflictModal: React.FC = () => {
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  const conflicts = useAuthStore((state) => state.pendingSyncConflicts);
  const setConflicts = useAuthStore((state) => state.setPendingSyncConflicts);
  const imageConflicts = useAuthStore((state) => state.pendingImageConflicts);
  const setImageConflicts = useAuthStore((state) => state.setPendingImageConflicts);
  const setSyncStatus = useAuthStore((state) => state.setSyncStatus);
  const setWarbands = useWarbandStore((state) => state.setWarbands);
  const [resolving, setResolving] = useState(false);
  const conflict = conflicts[0];

  const resolve = async (choice: 'local' | 'drive') => {
    if (!conflict || !accessToken || !user) return;
    setResolving(true);
    setSyncStatus('syncing');
    try {
      const nextImageConflicts = await manifestSyncService.resolveManifestConflict(
        accessToken,
        user.sub,
        conflict.entityKey,
        choice
      );
      if (nextImageConflicts.length > 0) {
        setImageConflicts(nextImageConflicts);
        setSyncStatus('idle');
        return;
      }
      setConflicts(conflicts.slice(1));
      setWarbands(getAllWarbands());
      const warbandState = useWarbandStore.getState();
      if (warbandState.selectedWarbandId) {
        await warbandState.fetchPlayWarbandData(warbandState.selectedWarbandId);
      }
      const referenceState = useReferenceDataStore.getState();
      referenceState.reloadCustomCreatures();
      referenceState.reloadCustomWeapons();
      referenceState.reloadCustomArmours();
      referenceState.reloadCustomBuffs();
      setSyncStatus(conflicts.length > 1 ? 'idle' : 'success');
    } catch (error) {
      setSyncStatus('error', error instanceof Error ? error.message : 'Conflict resolution failed');
    } finally {
      setResolving(false);
    }
  };

  if (!conflict || !accessToken || !user || imageConflicts.length > 0) return null;

  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown={resolving}>
      <DialogTitle>Sync conflict</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {entityLabel(conflict.entityKey)} changed on this device and another device.
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2">
            This device: {new Date(conflict.localModifiedAt).toLocaleString()}
          </Typography>
          <Typography variant="body2">
            Drive: {new Date(conflict.remote.modifiedAt).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Remote device: {conflict.remote.deviceId}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {resolving && <CircularProgress size={20} sx={{ mr: 'auto' }} />}
        <Button
          startIcon={<CloudIcon />}
          onClick={() => void resolve('drive')}
          disabled={resolving}
        >
          Use Drive
        </Button>
        <Button
          variant="contained"
          startIcon={<DeviceIcon />}
          onClick={() => void resolve('local')}
          disabled={resolving}
        >
          Use this device
        </Button>
      </DialogActions>
    </Dialog>
  );
};